/** `PlaylistStep.ContentType` from proto `playlist/playlist.proto`; the backend sends the number. */
export enum ContentType {
    Movie = 0,
    Image = 1,
    Html = 2,
    Text = 3,
    /** Nothing is displayed for the step's length. */
    Pause = 4,
    Playlist = 5,
    Layout = 6,
    /** Audio only; nothing is displayed. */
    Sound = 7,
    /** Reserved by DSM. */
    NotDefined = 8,
}

export type RawStep = {
    content_type: ContentType;
    sequence: number;
    sub_sequence: number;
    length: number;
    value: string;
    playlist_name?: string;
    /** Local time of day as HHMM (`04:03` is `403`) the step becomes visible. */
    day_start?: number | null;
    /** Local time of day as HHMM the step becomes invisible. */
    day_end?: number | null;
    /** Epoch seconds the step becomes visible. */
    start_date?: number | null;
    /** Epoch seconds the step becomes invisible. */
    end_date?: number | null;
    /** ISO weekday numbers the step is visible on, `1` = Monday … `7` = Sunday (e.g. `"12345"`). */
    weekdays?: string | null;
};

/** When a step may be shown; an absent bound does not restrict. */
export type StepSchedule = {
    /** Minutes since local midnight. */
    dayStart?: number;
    dayEnd?: number;
    /** Epoch seconds. */
    startDate?: number;
    endDate?: number;
    /** ISO weekdays, 1 = Monday … 7 = Sunday. */
    weekdays?: number[];
};

export type StepComponent =
    | "DataObjectImage"
    | "DataObjectMovie"
    | "DataObjectHtml"
    | "DataObjectText"
    | "DataObjectPlaylist"
    | "DataObjectSound";

export type Step = {
    length: number;
    sequence: number;
    sub_sequence: number;
    /** Vue component to render; `null` is a blank slot that still holds its length. */
    type: StepComponent | null;
    /** Props to pass to the component via v-bind */
    props: Record<string, any>;
    /** The nested playlist's name (`Playlist` steps). */
    format?: string;
    schedule: StepSchedule;
};

type ParentStyle = { width?: unknown; height?: unknown } | undefined;

const FILL = { width: "100%", height: "100%" };

/**
 * The step a playlist object `parentId` renders for `step`. Every content type yields a step, so the
 * rotation keeps the server's timing: types the client cannot display (LAYOUT, NOT_DEFINED, unknown
 * numbers) are blank like PAUSE.
 */
export function parseStep(step: RawStep, parentId: string, parentStyle: ParentStyle): Step {
    const id = `${parentId}.${step.sequence}.${step.sub_sequence}`;
    const base = {
        length: Number(step.length || 0),
        sequence: step.sequence,
        sub_sequence: step.sub_sequence,
        schedule: parseSchedule(step),
    };
    const item = (style: Record<string, unknown>) => ({
        props: { id, item: { props: { id, style: { ...style } }, published: { src: step.value } } },
    });

    switch (Number(step.content_type)) {
        case ContentType.Image:
            return {
                ...base,
                type: "DataObjectImage",
                ...item({
                    top: "0px",
                    left: "0px",
                    backgroundSize: "cover",
                    width: parentStyle?.width ?? "auto",
                    height: parentStyle?.height ?? "auto",
                }),
            };
        case ContentType.Movie:
            return { ...base, type: "DataObjectMovie", ...item(FILL) };
        case ContentType.Html:
            return { ...base, type: "DataObjectHtml", ...item(FILL) };
        case ContentType.Text:
            return { ...base, type: "DataObjectText", ...item(FILL) };
        case ContentType.Playlist:
            return { ...base, type: "DataObjectPlaylist", format: step.playlist_name, ...item(FILL) };
        case ContentType.Sound:
            return { ...base, type: "DataObjectSound", ...item(FILL) };
        default:
            // see the doc comment: Pause, Layout, NotDefined and unknown types
            return { ...base, type: null, props: { id } };
    }
}

/** `HHMM` (`1615`) as minutes since midnight; `undefined` when absent or not a valid time. */
function hhmmToMinutes(value: unknown): number | undefined {
    if (value === undefined || value === null || value === "") return undefined;
    const n = Number(value);
    if (!Number.isInteger(n) || n < 0) return undefined;
    const hours = Math.floor(n / 100);
    const minutes = n % 100;
    if (hours > 24 || minutes > 59 || (hours === 24 && minutes > 0)) return undefined;
    return hours * 60 + minutes;
}

/** Epoch seconds; `0`, absent or invalid is no bound. */
function epochSeconds(value: unknown): number | undefined {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function parseSchedule(step: RawStep): StepSchedule {
    const schedule: StepSchedule = {};
    let dayStart = hhmmToMinutes(step.day_start);
    let dayEnd = hhmmToMinutes(step.day_end);
    // An end of 00:00 is the end of the day, so 0..0 (both defaulted) is the whole day.
    if (dayEnd === 0) dayEnd = 24 * 60;
    if (dayStart === 0) dayStart = undefined;
    if (dayEnd === 24 * 60) dayEnd = undefined;
    if (dayStart !== undefined) schedule.dayStart = dayStart;
    if (dayEnd !== undefined) schedule.dayEnd = dayEnd;

    const startDate = epochSeconds(step.start_date);
    const endDate = epochSeconds(step.end_date);
    if (startDate !== undefined) schedule.startDate = startDate;
    if (endDate !== undefined) schedule.endDate = endDate;

    // 0 is accepted as Sunday too; anything else that is not 1..7 is ignored.
    const days = String(step.weekdays ?? "").match(/[0-7]/g);
    if (days) schedule.weekdays = Array.from(new Set(days.map((d) => Number(d) || 7))).sort();
    return schedule;
}

/**
 * Whether a step with `schedule` is visible at `now` (device local time). The date range is
 * `[startDate, endDate)`, the day window `[dayStart, dayEnd)`; a window whose start is after its end
 * spans midnight (`2200`..`0600`).
 */
export function isScheduled(schedule: StepSchedule, now: Date): boolean {
    const nowSec = now.getTime() / 1000;
    if (schedule.startDate !== undefined && nowSec < schedule.startDate) return false;
    if (schedule.endDate !== undefined && nowSec >= schedule.endDate) return false;

    if (schedule.weekdays && schedule.weekdays.length) {
        const isoDay = now.getDay() || 7;
        if (!schedule.weekdays.includes(isoDay)) return false;
    }

    const start = schedule.dayStart ?? 0;
    const end = schedule.dayEnd ?? 24 * 60;
    if (start === end) return true;
    const minute = now.getHours() * 60 + now.getMinutes();
    return start < end ? minute >= start && minute < end : minute >= start || minute < end;
}
